import _classCallCheck from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/createClass";

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Position } from '../core/position.js';
import { Range } from '../core/range.js';
import { InlineDecoration, ViewModelDecoration } from './viewModel.js';
import { filterValidationDecorations } from '../config/editorOptions.js';
export var ViewModelDecorations = /*#__PURE__*/function () {
  function ViewModelDecorations(editorId, model, configuration, linesCollection, coordinatesConverter) {
    _classCallCheck(this, ViewModelDecorations);

    this.editorId = editorId;
    this.model = model;
    this.configuration = configuration;
    this._linesCollection = linesCollection;
    this._coordinatesConverter = coordinatesConverter;
    this._decorationsCache = Object.create(null);
    this._cachedModelDecorationsResolver = null;
    this._cachedModelDecorationsResolverViewRange = null;
  }

  _createClass(ViewModelDecorations, [{
    key: "_clearCachedModelDecorationsResolver",
    value: function _clearCachedModelDecorationsResolver() {
      this._cachedModelDecorationsResolver = null;
      this._cachedModelDecorationsResolverViewRange = null;
    }
  }, {
    key: "dispose",
    value: function dispose() {
      this._decorationsCache = Object.create(null);

      this._clearCachedModelDecorationsResolver();
    }
  }, {
    key: "reset",
    value: function reset() {
      this._decorationsCache = Object.create(null);

      this._clearCachedModelDecorationsResolver();
    }
  }, {
    key: "onModelDecorationsChanged",
    value: function onModelDecorationsChanged() {
      this._decorationsCache = Object.create(null);

      this._clearCachedModelDecorationsResolver();
    }
  }, {
    key: "onLineMappingChanged",
    value: function onLineMappingChanged() {
      this._decorationsCache = Object.create(null);

      this._clearCachedModelDecorationsResolver();
    }
  }, {
    key: "_getOrCreateViewModelDecoration",
    value: function _getOrCreateViewModelDecoration(modelDecoration) {
      var id = modelDecoration.id;
      var r = this._decorationsCache[id];

      if (!r) {
        var modelRange = modelDecoration.range;
        var options = modelDecoration.options;
        var viewRange;

        if (options.isWholeLine) {
          var start = this._coordinatesConverter.convertModelPositionToViewPosition(new Position(modelRange.startLineNumber, 1));

          var end = this._coordinatesConverter.convertModelPositionToViewPosition(new Position(modelRange.endLineNumber, this.model.getLineMaxColumn(modelRange.endLineNumber)));

          viewRange = new Range(start.lineNumber, start.column, end.lineNumber, end.column);
        } else {
          viewRange = this._coordinatesConverter.convertModelRangeToViewRange(modelRange);
        }

        r = new ViewModelDecoration(viewRange, options);
        this._decorationsCache[id] = r;
      }

      return r;
    }
  }, {
    key: "getDecorationsViewportData",
    value: function getDecorationsViewportData(viewRange) {
      var cacheIsValid = this._cachedModelDecorationsResolver !== null;
      cacheIsValid = cacheIsValid && viewRange.equalsRange(this._cachedModelDecorationsResolverViewRange);

      if (!cacheIsValid) {
        this._cachedModelDecorationsResolver = this._getDecorationsViewportData(viewRange);
        this._cachedModelDecorationsResolverViewRange = viewRange;
      }

      return this._cachedModelDecorationsResolver;
    }
  }, {
    key: "_getDecorationsViewportData",
    value: function _getDecorationsViewportData(viewportRange) {
      var modelDecorations = this._linesCollection.getDecorationsInRange(viewportRange, this.editorId, filterValidationDecorations(this.configuration.options));

      var startLineNumber = viewportRange.startLineNumber;
      var endLineNumber = viewportRange.endLineNumber;
      var decorationsInViewport = [],
          decorationsInViewportLen = 0;
      var inlineDecorations = [];

      for (var j = startLineNumber; j <= endLineNumber; j++) {
        inlineDecorations[j - startLineNumber] = [];
      }

      for (var i = 0, len = modelDecorations.length; i < len; i++) {
        var modelDecoration = modelDecorations[i];
        var decorationOptions = modelDecoration.options;

        var viewModelDecoration = this._getOrCreateViewModelDecoration(modelDecoration);

        var viewRange = viewModelDecoration.range;
        decorationsInViewport[decorationsInViewportLen++] = viewModelDecoration;

        if (decorationOptions.inlineClassName) {
          var inlineDecoration = new InlineDecoration(viewRange, decorationOptions.inlineClassName, decorationOptions.inlineClassNameAffectsLetterSpacing ? 3
          /* RegularAffectingLetterSpacing */
          : 0
          /* Regular */
          );
          var intersectedStartLineNumber = Math.max(startLineNumber, viewRange.startLineNumber);
          var intersectedEndLineNumber = Math.min(endLineNumber, viewRange.endLineNumber);

          for (var _j = intersectedStartLineNumber; _j <= intersectedEndLineNumber; _j++) {
            inlineDecorations[_j - startLineNumber].push(inlineDecoration);
          }
        }

        if (decorationOptions.beforeContentClassName) {
          if (startLineNumber <= viewRange.startLineNumber && viewRange.startLineNumber <= endLineNumber) {
            var _inlineDecoration = new InlineDecoration(new Range(viewRange.startLineNumber, viewRange.startColumn, viewRange.startLineNumber, viewRange.startColumn), decorationOptions.beforeContentClassName, 1
            /* Before */
            );

            inlineDecorations[viewRange.startLineNumber - startLineNumber].push(_inlineDecoration);
          }
        }

        if (decorationOptions.afterContentClassName) {
          if (startLineNumber <= viewRange.endLineNumber && viewRange.endLineNumber <= endLineNumber) {
            var _inlineDecoration2 = new InlineDecoration(new Range(viewRange.endLineNumber, viewRange.endColumn, viewRange.endLineNumber, viewRange.endColumn), decorationOptions.afterContentClassName, 2
            /* After */
            );

            inlineDecorations[viewRange.endLineNumber - startLineNumber].push(_inlineDecoration2);
          }
        }
      }

      return {
        decorations: decorationsInViewport,
        inlineDecorations: inlineDecorations
      };
    }
  }]);

  return ViewModelDecorations;
}();